import * as React from "react"
import {
  CategoryType,
  PartyRole,
  type SortDirection,
  TransactionColumnName,
} from "@digits-graphql/frontend/graphql-bearer"
import { SvgCalendar } from "@digits-shared/components/SVGIcons/line/Calendar.svg"
import { SvgCurrencyDollarCircle } from "@digits-shared/components/SVGIcons/line/CurrencyDollarCircle.svg"
import { SvgLineHeight } from "@digits-shared/components/SVGIcons/line/LineHeight.svg"
import { useCategoryDetailsContext } from "src/frontend/components/OS/Details/Category/CategoryDetailsContext"
import { usePartyDetails } from "src/frontend/components/OS/Details/Party/PartyDetailsContext"
import type { DisplayOptionAccessor } from "src/frontend/components/OS/Shared/DisplayOptions/DisplayOption"
import { useTransactionsDisplayOptions } from "src/frontend/components/OS/Shared/DisplayOptions/TransactionsDisplayOptionsContext"
import { DimensionSortBy } from "src/frontend/types/DimensionFilter"

export function useDisplayCategoryFilter() {
  const { partyRole } = usePartyDetails()
  const { category } = useCategoryDetailsContext()

  return partyRole !== PartyRole.UnknownRole || category?.type
}

export function useDisplayOtherSideCategoryFilter() {
  const { partyRole } = usePartyDetails()
  const { category } = useCategoryDetailsContext()

  return (
    partyRole === PartyRole.EntityVendorRole ||
    category?.type === CategoryType.Expenses ||
    category?.type === CategoryType.OtherExpenses
  )
}

export function useSortDimensions() {
  const { sortBy, sortDirection } = useTransactionsDisplayOptions()

  const onSelect = React.useCallback(
    (value: TransactionColumnName) => {
      sortBy.set(value)
      sortDirection.clear()
    },
    [sortBy, sortDirection]
  )

  return React.useMemo(
    () =>
      [
        {
          title: "Date",
          TitleIconElement: SvgCalendar,
          value: TransactionColumnName.OccurredAt,
        },
        {
          title: "Amount",
          TitleIconElement: SvgCurrencyDollarCircle,
          value: TransactionColumnName.Amount,
        },
      ].map(({ value, ...rest }) => {
        const selected = sortBy.get() === value
        return {
          ...rest,
          selected,
          onSelect: () => onSelect(value),
        }
      }),
    [onSelect, sortBy]
  )
}

export function useDimensionSortElements(
  sortBy: DisplayOptionAccessor<DimensionSortBy>,
  sortDirection: DisplayOptionAccessor<SortDirection>
) {
  const onSelect = React.useCallback(
    (value: DimensionSortBy) => {
      sortBy.set(value)
      sortDirection.clear()
    },
    [sortBy, sortDirection]
  )

  return React.useMemo(
    () =>
      [
        {
          title: "Amount",
          TitleIconElement: SvgCurrencyDollarCircle,
          value: DimensionSortBy.Amount,
        },
        {
          title: "Name",
          TitleIconElement: SvgLineHeight,
          value: DimensionSortBy.Name,
        },
      ].map(({ value, ...rest }) => {
        const selected = sortBy.get() === value
        return {
          ...rest,
          selected,
          onSelect: () => onSelect(value),
        }
      }),
    [onSelect, sortBy]
  )
}
